import React, { useRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import DownloadDialog from '../DownloadDialog';
import { IconVideo } from './icons';
import SettingsPanel from './panels/SettingsPanel';
import PlaylistPanel from './panels/PlaylistPanel';
import FullscreenToggleButton from './panels/FullscreenToggleButton';
import TransportRow from './panels/TransportRow';
import ProgressRow from './panels/ProgressRow';
import SecondaryControlsRow from './panels/SecondaryControlsRow';
import { theme } from '../../ui/theme';
import { isInstrumentalMusic } from '../../utils/musicMeta';

/**
 * 说明：这是从 `MusicPlayer.js` 抽出来的“主播放器面板 UI”。
 * - 尽量保持内部 JSX/变量名不变，以降低拆分风险
 * - 所需状态/refs/handler 由父组件通过 props 传入
 */
export default function PlayerPanel(props) {
  const {
    // 基础依赖
    t,
    i18n,
    cx,
    spaNavigate,
    showUiHint,

    // refs
    videoRef,
    currentTimeTextRefDesktop,
    currentTimeTextRefMobile,
    progressTrackRef,
    progressFillRef,
    progressKnobRef,
    settingsButtonRef,
    settingsPanelRef,
    playlistPanelRef,
    desktopControlsRowRef,
    volumeSliderWrapRef,

    // 状态/数据
    currentMusic,
    playlist,
    playing,
    loading,
    duration,
    isFullscreen,
    isVideoFullscreen,
    isMobileViewport,
    isMdToLg,
    isLgUp,
    hideVolumeSlider,
    isMinimized,
    hasVideoInCurrentMusic,
    hasVideo,
    showPlaylist,
    setShowPlaylist,
    showSettings,
    setShowSettings,
    showVideoWindow,
    setShowVideoWindow,
    setManualHidden,
    isCached,
    showFileInfo,
    setShowFileInfo,
    fileInfoPosition,
    setFileInfoPosition,
    currentPlayingFormat,
    lyricSettings,
    setLyricSettings,
    playMode,
    muted,
    volume,
    playlistLoop,
    setPlaylistLoop,
    sleepTimer,
    sleepTimerMinutes,
    fadeInOut,
    setFadeInOut,
    selectedAudioOutputId,
    setSelectedAudioOutputId,
    audioOutputDevices,
    audioFocusMode,
    setAudioFocusMode,

    // handlers / helpers
    getCoverStreamUrlLocal,
    getCurrentLanguageTitle,
    formatFileSize,
    formatBitrate,
    formatSampleRate,
    formatTime,
    handleButtonClickWithHint,
    handlePrevious,
    handleNext,
    togglePlay,
    handleSeekPointerDown,
    handleSeekPointerMove,
    handleSeekPointerUpOrCancel,
    togglePlayMode,
    handleVolumeChange,
    toggleMute,
    handleDownload,
    handleShare,
    ensureVideoMediaInfo,
    onPlaylistChange,
    onRemoveFromPlaylist,
    setPlaylist,
    setSleepTimerHandler,
    setAudioOutputDevice,
    enumerateAudioOutputDevices,
    isMobileDevice,
    handleDownloadConfirm,
    downloadDialogOpen
  } = props;

  // 统一播放器按钮尺寸（按钮 32px；ICON 20px）
  const iconClass = 'w-5 h-5';
  const iconButtonBase = 'h-8 w-8 inline-flex items-center justify-center rounded-md transition-all duration-150 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/25 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b1220] active:scale-[0.98]';
  // 统一为播放器绿色半透明底/描边（更“玻璃”）
  const iconButtonIdle = 'bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-400/25 text-white/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]';
  const iconButtonActive = 'bg-emerald-600/90 hover:bg-emerald-600 text-white shadow-[0_8px_18px_rgba(16,185,129,0.25)]';
  const iconButtonMuted = 'bg-rose-500/20 hover:bg-rose-500/25 border border-rose-400/40 text-rose-100 shadow-[0_8px_18px_rgba(244,63,94,0.18)]';
  const iconButtonDisabled = 'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-500/10 disabled:active:scale-100';
  const controlText = 'text-[11px] text-white/55 tabular-nums';
  const coverUrl = currentMusic ? (getCoverStreamUrlLocal(currentMusic) || currentMusic.cover_image || null) : null;
  const showInstrumentalBadge = !hasVideoInCurrentMusic && isInstrumentalMusic(currentMusic);

  // 手势识别相关状态
  const touchStartRef = useRef(null);
  const mouseStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const containerRef = useRef(null);
  const fileInfoPanelRef = useRef(null);
  const MIN_SWIPE_DISTANCE = 30; // 最小滑动距离（px）
  const [dragVisual, setDragVisual] = useState({ active: false, deltaY: 0 });
  const [mouseDragActive, setMouseDragActive] = useState(false);

  useLayoutEffect(() => {
    if (!showFileInfo) return;
    const panel = fileInfoPanelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const padding = 8;
    let offsetX = 0;
    let offsetY = 0;

    if (rect.right > window.innerWidth - padding) {
      offsetX = (window.innerWidth - padding) - rect.right;
    } else if (rect.left < padding) {
      offsetX = padding - rect.left;
    }

    if (rect.top < padding) {
      offsetY = padding - rect.top;
    } else if (rect.bottom > window.innerHeight - padding) {
      offsetY = (window.innerHeight - padding) - rect.bottom;
    }

    if (offsetX || offsetY) {
      setFileInfoPosition(prev => {
        const nextTop = prev.top + offsetY;
        const nextLeft = prev.left + offsetX;
        if (nextTop === prev.top && nextLeft === prev.left) return prev;
        return { top: nextTop, left: nextLeft };
      });
    }
  }, [showFileInfo, fileInfoPosition.top, fileInfoPosition.left, setFileInfoPosition]);

  // 处理触摸开始
  const handleTouchStart = useCallback((e) => {
    // 如果触摸点在可交互元素上（按钮、输入框等），不处理手势
    const target = e.target;
    if (playlistPanelRef?.current && playlistPanelRef.current.contains(target)) {
      return;
    }
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || 
        target.closest('button') || target.closest('input') || target.closest('textarea')) {
      return;
    }
    if (e.touches.length === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now()
      };
      isDraggingRef.current = false;
    }
  }, [playlistPanelRef]);

  // 处理触摸移动（原生事件，非 passive）
  const handleTouchMoveNative = useCallback((e) => {
    if (!touchStartRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // 如果垂直移动距离大于水平移动距离，认为是垂直滑动
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
      isDraggingRef.current = true;
      e.preventDefault(); // 防止页面滚动（使用原生事件监听器，可以调用 preventDefault）
      setDragVisual({
        active: true,
        deltaY: Math.max(Math.min(deltaY, 120), -120)
      });
    }
  }, [playlistPanelRef]);

  // React 事件处理（用于兼容性，但不调用 preventDefault）
  const handleTouchMove = useCallback((e) => {
    if (!touchStartRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // 如果垂直移动距离大于水平移动距离，认为是垂直滑动
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
      isDraggingRef.current = true;
      setDragVisual({
        active: true,
        deltaY: Math.max(Math.min(deltaY, 120), -120)
      });
    }
  }, [playlistPanelRef]);

  // 处理触摸结束
  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) {
      touchStartRef.current = null;
      return;
    }

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // 判断是否为有效的滑动手势（即使没有标记为拖拽，也检查距离）
    if (distance > MIN_SWIPE_DISTANCE && deltaTime < 500 && Math.abs(deltaY) > Math.abs(deltaX)) {
      // 向下滑动
      if (deltaY > MIN_SWIPE_DISTANCE) {
        // 只在事件可取消时才调用 preventDefault
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
        if (showPlaylist) {
          // 如果歌曲列表已经显示，隐藏歌曲列表
          if (setShowPlaylist) setShowPlaylist(false);
        } else {
          // 否则，隐藏播放器
          if (setManualHidden) setManualHidden(true);
        }
      }
      // 向上滑动
      else if (deltaY < -MIN_SWIPE_DISTANCE) {
        // 只在事件可取消时才调用 preventDefault
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
        if (!showVideoWindow) {
          // 如果封面窗口或视频窗口没有显示，显示它们
          if (setShowVideoWindow) setShowVideoWindow(true);
        } else if (!showPlaylist) {
          // 否则，如果歌曲列表没有显示，显示歌曲列表
          if (setShowPlaylist) setShowPlaylist(true);
        }
      }
    }

    touchStartRef.current = null;
    isDraggingRef.current = false;
    setDragVisual({ active: false, deltaY: 0 });
  }, [showPlaylist, setShowPlaylist, showVideoWindow, setShowVideoWindow, setManualHidden]);

  // 处理鼠标按下
  const handleMouseDown = useCallback((e) => {
    // 如果点击在可交互元素上，不处理手势
    const target = e.target;
    if (playlistPanelRef?.current && playlistPanelRef.current.contains(target)) {
      return;
    }
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || 
        target.closest('button') || target.closest('input') || target.closest('textarea')) {
      return;
    }
    if (e.button === 0) { // 左键
      mouseStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        time: Date.now()
      };
      isDraggingRef.current = false;
      setMouseDragActive(true);
    }
  }, []);

  // 处理鼠标移动
  const handleMouseMove = useCallback((e) => {
    if (!mouseStartRef.current) return;
    const deltaX = e.clientX - mouseStartRef.current.x;
    const deltaY = e.clientY - mouseStartRef.current.y;
    
    // 如果垂直移动距离大于水平移动距离，认为是垂直拖拽
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
      isDraggingRef.current = true;
      setDragVisual({
        active: true,
        deltaY: Math.max(Math.min(deltaY, 120), -120)
      });
    }
  }, []);

  // 处理鼠标抬起
  const handleMouseUp = useCallback((e) => {
    if (!mouseStartRef.current) {
      mouseStartRef.current = null;
      setMouseDragActive(false);
      return;
    }

    const deltaX = e.clientX - mouseStartRef.current.x;
    const deltaY = e.clientY - mouseStartRef.current.y;
    const deltaTime = Date.now() - mouseStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // 判断是否为有效的拖拽手势（即使没有标记为拖拽，也检查距离）
    if (distance > MIN_SWIPE_DISTANCE && deltaTime < 500 && Math.abs(deltaY) > Math.abs(deltaX)) {
      // 向下拖拽
      if (deltaY > MIN_SWIPE_DISTANCE) {
        e.preventDefault();
        e.stopPropagation();
        if (showPlaylist) {
          // 如果歌曲列表已经显示，隐藏歌曲列表
          if (setShowPlaylist) setShowPlaylist(false);
        } else {
          // 否则，隐藏播放器
          if (setManualHidden) setManualHidden(true);
        }
      }
      // 向上拖拽
      else if (deltaY < -MIN_SWIPE_DISTANCE) {
        e.preventDefault();
        e.stopPropagation();
        if (!showVideoWindow) {
          // 如果封面窗口或视频窗口没有显示，显示它们
          if (setShowVideoWindow) setShowVideoWindow(true);
        } else if (!showPlaylist) {
          // 否则，如果歌曲列表没有显示，显示歌曲列表
          if (setShowPlaylist) setShowPlaylist(true);
        }
      }
    }

    mouseStartRef.current = null;
    isDraggingRef.current = false;
    setDragVisual({ active: false, deltaY: 0 });
    setMouseDragActive(false);
  }, [showPlaylist, setShowPlaylist, showVideoWindow, setShowVideoWindow, setManualHidden]);

  // 处理鼠标离开（防止鼠标移出窗口时状态未重置）
  const handleMouseLeave = useCallback(() => {
    mouseStartRef.current = null;
    isDraggingRef.current = false;
    setDragVisual({ active: false, deltaY: 0 });
    setMouseDragActive(false);
  }, []);

  // 处理触摸开始（原生事件，非 passive）
  const handleTouchStartNative = useCallback((e) => {
    // 如果触摸点在可交互元素上（按钮、输入框等），不处理手势
    const target = e.target;
    if (playlistPanelRef?.current && playlistPanelRef.current.contains(target)) {
      return;
    }
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || 
        target.closest('button') || target.closest('input') || target.closest('textarea')) {
      return;
    }
    if (e.touches.length === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now()
      };
      isDraggingRef.current = false;
    }
  }, []);

  // 使用原生事件监听器来处理触摸事件（非 passive，可以调用 preventDefault）
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStartNative, { passive: false });
    container.addEventListener('touchmove', handleTouchMoveNative, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStartNative);
      container.removeEventListener('touchmove', handleTouchMoveNative);
    };
  }, [handleTouchStartNative, handleTouchMoveNative]);

  useEffect(() => {
    if (!mouseDragActive) return;
    const handleMove = (e) => handleMouseMove(e);
    const handleUp = (e) => handleMouseUp(e);
    const handleBlur = () => handleMouseLeave();

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [handleMouseLeave, handleMouseMove, handleMouseUp, mouseDragActive]);

  return (
    <>
      {process.env.NODE_ENV === 'development' && (
        <div
          className="absolute bottom-1 right-1 text-[10px] text-gray-300/80 bg-black/35 px-2 py-1 rounded-md tabular-nums pointer-events-none"
          data-no-toggle
        >
          {props.devViewport?.w}×{props.devViewport?.h}
        </div>
      )}

      <FullscreenToggleButton
        t={t}
        cx={cx}
        hasVideo={hasVideo}
        isMobileViewport={isMobileViewport}
        isFullscreen={isFullscreen}
        setIsFullscreen={props.setIsFullscreen}
        isVideoFullscreen={isVideoFullscreen}
        setIsVideoFullscreen={props.setIsVideoFullscreen}
        showVideoWindow={props.showVideoWindow}
        setShowVideoWindow={props.setShowVideoWindow}
        videoWindowStyle={props.videoWindowStyle}
      />

      <div
        ref={containerRef}
        className={`container mx-auto px-3 sm:px-4 py-2.5 relative ${
          isFullscreen ? 'flex-1 min-h-0 flex flex-col z-10' : ''
        }`}
        style={{
          transform: `translate3d(0, ${dragVisual.deltaY}px, 0)`,
          transition: dragVisual.active ? 'transform 16ms linear' : 'transform 180ms ease-out'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {/* 主控制区 */}
        <div
          className={cx(
            'flex gap-4',
            isFullscreen ? 'flex-shrink-0' : '',
            isMinimized
              ? 'flex-col items-center gap-2'
              : 'flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-4'
          )}
          style={isFullscreen ? { position: 'relative', zIndex: 10 } : {}}
        >
          {/* ===== 手机：歌曲信息区（左） + 三个播放按钮（中间） ===== */}
          {!isMinimized && (
            <div className="w-full flex items-center gap-3 md:hidden">
              <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden relative">
                <div className="relative w-16 h-16 flex-shrink-0">
                  {coverUrl ? (
                    <img
                      src={coverUrl}
                      alt={getCurrentLanguageTitle(currentMusic)}
                      className="w-16 h-16 rounded object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className={['w-16 h-16 rounded flex items-center justify-center', theme.surfaces.glassItem].join(' ')}>
                      <svg className="w-8 h-8 text-white/40" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                      </svg>
                    </div>
                  )}
                  {showInstrumentalBadge && (
                    <img
                      src="/icons/mcd.png"
                      alt={t('musicList.categoryInstrumental', '纯音乐')}
                      title={t('musicList.categoryInstrumental', '纯音乐')}
                      className="absolute top-0 right-0 w-5 h-5 object-contain"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1 relative">
                  <p className="text-sm font-semibold truncate flex items-center gap-1 min-w-0">
                    {hasVideoInCurrentMusic && (
                      <img
                        src="/icons/mtv-s.png"
                        alt="MTV"
                        className="w-5 h-5 flex-shrink-0 object-contain"
                        title={t('player.hasVideo') || '视频'}
                      />
                    )}
                    {!hasVideoInCurrentMusic && showInstrumentalBadge && (
                      <img
                        src="/icons/mcd-s.png"
                        alt={t('musicList.categoryInstrumental', '纯音乐')}
                        title={t('musicList.categoryInstrumental', '纯音乐')}
                        className="w-5 h-5 flex-shrink-0 object-contain"
                      />
                    )}
                    <span className="truncate min-w-0">{getCurrentLanguageTitle(currentMusic)}</span>
                    {isCached && (
                      <img
                        src="/icons/regular_smile.png"
                        alt={t('player.cached')}
                        className="w-4 h-4 flex-shrink-0 cursor-pointer"
                        title={t('musicList.cachedOffline')}
                        onClick={(e) => {
                          e.stopPropagation();
                          showUiHint(t('musicList.cachedOffline'), { anchorEl: e.currentTarget });
                        }}
                      />
                    )}
                  </p>
                  {currentMusic.album && (
                    <p className="text-xs text-white/60 truncate">{currentMusic.album}</p>
                  )}
                  {/* 文件信息显示 */}
                  <div className="mt-1 flex items-center gap-2 text-xs text-white/45">
                    <button
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        ensureVideoMediaInfo();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setFileInfoPosition({ top: rect.top, left: rect.left });
                        setShowFileInfo(true);
                      }}
                      onMouseLeave={(e) => {
                        e.stopPropagation();
                        setTimeout(() => {
                          const infoPanel = document.querySelector('.file-info-panel:hover');
                          if (!infoPanel) setShowFileInfo(false);
                        }, 100);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (showFileInfo) {
                          setShowFileInfo(false);
                        } else {
                          ensureVideoMediaInfo();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setFileInfoPosition({ top: rect.top, left: rect.left });
                          setShowFileInfo(true);
                        }
                      }}
                      className="hover:text-white/80 transition-colors"
                      title={t('player.viewFileInfo')}
                      data-no-toggle
                    >
                      <svg className="w-3.5 h-3.5 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      {currentPlayingFormat?.format?.toUpperCase() || currentMusic.file_format?.toUpperCase() || t('player.unknownFormat')}
                      {currentPlayingFormat?.file_size
                        ? ` • ${formatFileSize(currentPlayingFormat.file_size)}`
                        : currentMusic.file_size
                        ? ` • ${formatFileSize(currentMusic.file_size)}`
                        : ''}
                    </button>
                  </div>

                  {/* 详细信息面板 - 浮动显示（使用 Portal 渲染到 body） */}
                  {showFileInfo && createPortal(
                    <div
                      ref={fileInfoPanelRef}
                      className={[
                        'file-info-panel fixed z-[10000] p-3 rounded-lg shadow-xl text-xs space-y-1 min-w-[280px] max-h-[70vh] overflow-auto pointer-events-auto',
                        theme.surfaces.modalPanel,
                        'text-white/60'
                      ].join(' ')}
                      style={{
                        top: `${fileInfoPosition.top}px`,
                        left: `${fileInfoPosition.left}px`,
                        transform: 'translateY(calc(-100% - 10px))'
                      }}
                      onMouseEnter={(e) => { e.stopPropagation(); setShowFileInfo(true); }}
                      onMouseLeave={(e) => { e.stopPropagation(); setShowFileInfo(false); }}
                      onClick={(e) => { e.stopPropagation(); setShowFileInfo(false); }}
                      data-no-toggle
                    >
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-white/45">{t('player.fileFormat')}</span>
                          <span className="ml-1 text-white/80">{currentPlayingFormat?.format?.toUpperCase() || currentMusic.file_format?.toUpperCase() || t('player.unknown')}</span>
                        </div>
                        <div>
                          <span className="text-white/45">{t('player.fileSize')}</span>
                          <span className="ml-1">
                            {currentPlayingFormat?.file_size
                              ? formatFileSize(currentPlayingFormat.file_size)
                              : currentMusic.file_size
                              ? formatFileSize(currentMusic.file_size)
                              : t('player.unknown')}
                          </span>
                        </div>
                        {(currentPlayingFormat?.bitrate || currentMusic.bitrate) && (
                          <div>
                            <span className="text-white/45">{t('player.bitrate')}</span>
                            <span className="ml-1">{formatBitrate(currentPlayingFormat?.bitrate || currentMusic.bitrate)}</span>
                          </div>
                        )}
                        {(currentPlayingFormat?.sample_rate || currentMusic.sample_rate) && (
                          <div>
                            <span className="text-white/45">{t('player.sampleRate')}</span>
                            <span className="ml-1">{formatSampleRate(currentPlayingFormat?.sample_rate || currentMusic.sample_rate)}</span>
                          </div>
                        )}
                        {(currentPlayingFormat?.channels || currentMusic.channels) && (
                          <div>
                            <span className="text-white/45">{t('player.channels')}</span>
                            <span className="ml-1">
                              {(() => {
                                const ch = currentPlayingFormat?.channels || currentMusic.channels;
                                return ch === 1 ? t('player.mono') :
                                  ch === 2 ? t('player.stereo') :
                                    t('player.channelsCount', { count: ch });
                              })()}
                            </span>
                          </div>
                        )}
                        {(currentPlayingFormat?.codec || currentMusic.metadata?.codec) && (
                          <div>
                            <span className="text-white/45">{t('player.codec')}</span>
                            <span className="ml-1">{currentPlayingFormat?.codec || currentMusic.metadata?.codec}</span>
                          </div>
                        )}

                        {/* 视频模式：显示更多视频/音频流信息 */}
                        {hasVideoInCurrentMusic && (() => {
                          const meta = (() => {
                            try {
                              const raw = currentMusic?.metadata;
                              return typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
                            } catch (_) {
                              return {};
                            }
                          })();

                          const vCodec = meta?.video_codec || meta?.videoCodec || meta?.vcodec || null;
                          const vBitrate = meta?.video_bitrate || meta?.videoBitrate || meta?.vbitrate || null;
                          const aCodec = meta?.audio_codec || meta?.audioCodec || meta?.acodec || null;
                          const aBitrate = meta?.audio_bitrate || meta?.audioBitrate || meta?.abitrate || null;

                          const vw = videoRef.current?.videoWidth || meta?.video_width || meta?.width || null;
                          const vh = videoRef.current?.videoHeight || meta?.video_height || meta?.height || null;

                          const hasAny =
                            !!vCodec || !!vBitrate || !!aCodec || !!aBitrate || (!!vw && !!vh);
                          if (!hasAny) return null;

                          return (
                            <>
                              <div className="col-span-2 pt-2 mt-1 border-t border-white/10 text-white/45">
                                视频/音频流
                              </div>
                              {(vw && vh) && (
                                <div className="col-span-2">
                                  <span className="text-white/45">分辨率</span>
                                  <span className="ml-1">{vw}×{vh}</span>
                                </div>
                              )}
                              <div>
                                <span className="text-white/45">视频编码</span>
                                <span className="ml-1">{vCodec || t('player.unknown')}</span>
                              </div>
                              <div>
                                <span className="text-white/45">视频码率</span>
                                <span className="ml-1">
                                  {vBitrate ? formatBitrate(vBitrate) : (currentPlayingFormat?.bitrate ? formatBitrate(currentPlayingFormat.bitrate) : t('player.unknown'))}
                                </span>
                              </div>
                              <div>
                                <span className="text-white/45">音频编码</span>
                                <span className="ml-1">{aCodec || t('player.unknown')}</span>
                              </div>
                              <div>
                                <span className="text-white/45">音频码率</span>
                                <span className="ml-1">{aBitrate ? formatBitrate(aBitrate) : t('player.unknown')}</span>
                              </div>
                            </>
                          );
                        })()}

                        {duration > 0 && (
                          <div>
                            <span className="text-white/45">{t('player.duration')}</span>
                            <span className="ml-1">{formatTime(duration)}</span>
                          </div>
                        )}
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              </div>

              <TransportRow
                variant="mobile"
                cx={cx}
                t={t}
                playlist={playlist}
                playing={playing}
                loading={loading}
                togglePlay={togglePlay}
                handlePrevious={handlePrevious}
                handleNext={handleNext}
                handleButtonClickWithHint={handleButtonClickWithHint}
                iconClass={iconClass}
                iconButtonBase={iconButtonBase}
                iconButtonIdle={iconButtonIdle}
                iconButtonDisabled={iconButtonDisabled}
              />
            </div>
          )}

          {/* ===== 桌面：封面和基本信息（左侧） ===== */}
          {!isMinimized && (
            <div className="hidden md:flex items-center justify-center md:justify-start gap-3 flex-shrink-0">
              <div className="relative">
                {coverUrl ? (
                  <img
                    src={coverUrl}
                    alt={getCurrentLanguageTitle(currentMusic)}
                    className="w-16 h-16 rounded object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className={['w-16 h-16 rounded flex items-center justify-center', theme.surfaces.glassItem].join(' ')}>
                    <svg className="w-8 h-8 text-white/40" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                    </svg>
                  </div>
                )}
                {showInstrumentalBadge && (
                  <img
                    src="/icons/mcd.png"
                    alt={t('musicList.categoryInstrumental', '纯音乐')}
                    title={t('musicList.categoryInstrumental', '纯音乐')}
                    className="absolute top-0 right-0 w-5 h-5 object-contain"
                  />
                )}
              </div>
              <div className="min-w-0 flex-1 relative">
                <p className="text-sm font-semibold truncate flex items-center gap-1">
                  {hasVideoInCurrentMusic && (
                    <img
                      src="/icons/mtv-s.png"
                      alt="MTV"
                      className="w-5 h-5 flex-shrink-0 object-contain"
                      title={t('player.hasVideo') || '视频'}
                    />
                  )}
                  {!hasVideoInCurrentMusic && showInstrumentalBadge && (
                    <img
                      src="/icons/mcd-s.png"
                      alt={t('musicList.categoryInstrumental', '纯音乐')}
                      title={t('musicList.categoryInstrumental', '纯音乐')}
                      className="w-5 h-5 flex-shrink-0 object-contain"
                    />
                  )}
                  <span className="truncate">{getCurrentLanguageTitle(currentMusic)}</span>
                  {isCached && (
                    <img
                      src="/icons/regular_smile.png"
                      alt={t('player.cached')}
                      className="w-4 h-4 flex-shrink-0 cursor-pointer"
                      title={t('musicList.cachedOffline')}
                      onClick={(e) => {
                        e.stopPropagation();
                        showUiHint(t('musicList.cachedOffline'), { anchorEl: e.currentTarget });
                      }}
                    />
                  )}
                </p>
                {currentMusic.album && (
                  <p className="text-xs text-white/60 truncate">{currentMusic.album}</p>
                )}
                {/* 文件信息显示 */}
                <div className="mt-1 flex items-center gap-2 text-xs text-white/45">
                  <button
                    onMouseEnter={(e) => {
                      e.stopPropagation();
                      ensureVideoMediaInfo();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setFileInfoPosition({ top: rect.top, left: rect.left });
                      setShowFileInfo(true);
                    }}
                    onMouseLeave={(e) => {
                      e.stopPropagation();
                      setTimeout(() => {
                        const infoPanel = document.querySelector('.file-info-panel:hover');
                        if (!infoPanel) setShowFileInfo(false);
                      }, 100);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (showFileInfo) {
                        setShowFileInfo(false);
                      } else {
                        ensureVideoMediaInfo();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setFileInfoPosition({ top: rect.top, left: rect.left });
                        setShowFileInfo(true);
                      }
                    }}
                    className="hover:text-white/80 transition-colors"
                    title={t('player.viewFileInfo')}
                    data-no-toggle
                  >
                    <svg className="w-3.5 h-3.5 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    {currentPlayingFormat?.format?.toUpperCase() || currentMusic.file_format?.toUpperCase() || t('player.unknownFormat')}
                    {currentPlayingFormat?.file_size
                      ? ` • ${formatFileSize(currentPlayingFormat.file_size)}`
                      : currentMusic.file_size
                      ? ` • ${formatFileSize(currentMusic.file_size)}`
                      : ''}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 缩小模式下的基本信息 */}
          {isMinimized && (
            <div className="min-w-0 text-center">
              <p className="text-sm font-semibold truncate flex items-center justify-center gap-1">
                {hasVideoInCurrentMusic && (
                  <IconVideo
                    className="w-5 h-5 flex-shrink-0 text-red-500"
                    title={t('player.hasVideo') || '视频'}
                  />
                )}
                {!hasVideoInCurrentMusic && showInstrumentalBadge && (
                  <img
                    src="/icons/mcd-s.png"
                    alt={t('musicList.categoryInstrumental', '纯音乐')}
                    title={t('musicList.categoryInstrumental', '纯音乐')}
                    className="w-5 h-5 flex-shrink-0 object-contain"
                  />
                )}
                <span className="truncate">{getCurrentLanguageTitle(currentMusic)}</span>
                {isCached && (
                  <img
                    src="/icons/regular_smile.png"
                    alt={t('player.cached')}
                    className="w-4 h-4 flex-shrink-0 cursor-pointer"
                    title={t('player.cachedOffline')}
                    onClick={(e) => {
                      e.stopPropagation();
                      showUiHint(t('musicList.cachedOffline'), { anchorEl: e.currentTarget });
                    }}
                  />
                )}
              </p>
              {currentMusic.album && (
                <p className="text-xs text-white/60 truncate">{currentMusic.album}</p>
              )}
            </div>
          )}

          {/* 播放控制（桌面显示；手机端三按钮已挪到歌曲信息右侧） */}
          <div className={cx('flex-1 flex flex-col items-center gap-2', isMinimized ? 'w-full' : 'hidden md:flex')} data-no-toggle>
            <TransportRow
              variant="desktop"
              cx={cx}
              t={t}
              playlist={playlist}
              playing={playing}
              loading={loading}
              togglePlay={togglePlay}
              handlePrevious={handlePrevious}
              handleNext={handleNext}
              handleButtonClickWithHint={handleButtonClickWithHint}
              iconClass={iconClass}
              iconButtonBase={iconButtonBase}
              iconButtonIdle={iconButtonIdle}
              iconButtonDisabled={iconButtonDisabled}
            />

            {!isMinimized && (
              <ProgressRow
                variant="desktop"
                cx={cx}
                controlText={controlText}
                currentTimeTextRef={currentTimeTextRefDesktop}
                progressTrackRef={progressTrackRef}
                progressFillRef={progressFillRef}
                progressKnobRef={progressKnobRef}
                handleSeekPointerDown={handleSeekPointerDown}
                handleSeekPointerMove={handleSeekPointerMove}
                handleSeekPointerUpOrCancel={handleSeekPointerUpOrCancel}
                duration={duration}
                formatTime={formatTime}
              />
            )}
          </div>

          {/* ===== 手机：进度条（单独一行） ===== */}
          {!isMinimized && (
            <div className="md:hidden w-full">
              <ProgressRow
                variant="mobile"
                cx={cx}
                controlText={controlText}
                currentTimeTextRef={currentTimeTextRefMobile}
                progressTrackRef={progressTrackRef}
                progressFillRef={progressFillRef}
                progressKnobRef={progressKnobRef}
                handleSeekPointerDown={handleSeekPointerDown}
                handleSeekPointerMove={handleSeekPointerMove}
                handleSeekPointerUpOrCancel={handleSeekPointerUpOrCancel}
                duration={duration}
                formatTime={formatTime}
              />
            </div>
          )}

          {/* ===== 手机：功能按钮一行（不显示音量滑条） ===== */}
          {!isMinimized && (
            <div className="relative">
              <SecondaryControlsRow
                variant="mobile"
                cx={cx}
                t={t}
                currentMusic={currentMusic}
                playlist={playlist}
                showPlaylist={showPlaylist}
                setShowPlaylist={setShowPlaylist}
                playMode={playMode}
                togglePlayMode={togglePlayMode}
                muted={muted}
                volume={volume}
                toggleMute={toggleMute}
                handleVolumeChange={handleVolumeChange}
                showSettings={showSettings}
                setShowSettings={setShowSettings}
                settingsButtonRef={settingsButtonRef}
                handleButtonClickWithHint={handleButtonClickWithHint}
                iconClass={iconClass}
                iconButtonBase={iconButtonBase}
                iconButtonIdle={iconButtonIdle}
                iconButtonActive={iconButtonActive}
                iconButtonMuted={iconButtonMuted}
                iconButtonDisabled={iconButtonDisabled}
                handleDownload={handleDownload}
                handleShare={handleShare}
              />
              {/* 设置面板：使用 Portal 渲染到 body（移动端） */}
              <SettingsPanel
                showSettings={showSettings}
                settingsPanelRef={settingsPanelRef}
                settingsButtonRef={settingsButtonRef}
                t={t}
                i18n={i18n}
                fadeInOut={fadeInOut}
                setFadeInOut={setFadeInOut}
                playlistLoop={playlistLoop}
                setPlaylistLoop={setPlaylistLoop}
                sleepTimer={sleepTimer}
                sleepTimerMinutes={sleepTimerMinutes}
                setSleepTimerHandler={setSleepTimerHandler}
                selectedAudioOutputId={selectedAudioOutputId}
                setSelectedAudioOutputId={setSelectedAudioOutputId}
                setAudioOutputDevice={setAudioOutputDevice}
                audioOutputDevices={audioOutputDevices}
                enumerateAudioOutputDevices={enumerateAudioOutputDevices}
                showUiHint={showUiHint}
                isMobileDevice={isMobileDevice}
                audioFocusMode={audioFocusMode}
                setAudioFocusMode={setAudioFocusMode}
                lyricSettings={lyricSettings}
                setLyricSettings={setLyricSettings}
              />
            </div>
          )}

          {/* ===== 桌面：右侧控制（含音量滑条） ===== */}
          {!isMinimized && (
            <div ref={desktopControlsRowRef} className="relative">
              <SecondaryControlsRow
                variant="desktop"
                cx={cx}
                t={t}
                currentMusic={currentMusic}
                playlist={playlist}
                showPlaylist={showPlaylist}
                setShowPlaylist={setShowPlaylist}
                playMode={playMode}
                togglePlayMode={togglePlayMode}
                muted={muted}
                volume={volume}
                toggleMute={toggleMute}
                handleVolumeChange={handleVolumeChange}
                showSettings={showSettings}
                setShowSettings={setShowSettings}
                settingsButtonRef={settingsButtonRef}
                handleButtonClickWithHint={handleButtonClickWithHint}
                iconClass={iconClass}
              iconButtonBase={iconButtonBase}
              iconButtonIdle={iconButtonIdle}
              iconButtonActive={iconButtonActive}
              iconButtonMuted={iconButtonMuted}
              iconButtonDisabled={iconButtonDisabled}
                isMdToLg={isMdToLg}
                isLgUp={isLgUp}
                hideVolumeSlider={hideVolumeSlider}
                volumeSliderWrapRef={volumeSliderWrapRef}
                handleDownload={handleDownload}
                handleShare={handleShare}
              />
              {/* 设置面板：使用 Portal 渲染到 body */}
              <SettingsPanel
                showSettings={showSettings}
                settingsPanelRef={settingsPanelRef}
                settingsButtonRef={settingsButtonRef}
                t={t}
                i18n={i18n}
                fadeInOut={fadeInOut}
                setFadeInOut={setFadeInOut}
                playlistLoop={playlistLoop}
                setPlaylistLoop={setPlaylistLoop}
                sleepTimer={sleepTimer}
                sleepTimerMinutes={sleepTimerMinutes}
                setSleepTimerHandler={setSleepTimerHandler}
                selectedAudioOutputId={selectedAudioOutputId}
                setSelectedAudioOutputId={setSelectedAudioOutputId}
                setAudioOutputDevice={setAudioOutputDevice}
                audioOutputDevices={audioOutputDevices}
                enumerateAudioOutputDevices={enumerateAudioOutputDevices}
                showUiHint={showUiHint}
                isMobileDevice={isMobileDevice}
                audioFocusMode={audioFocusMode}
                setAudioFocusMode={setAudioFocusMode}
                lyricSettings={lyricSettings}
                setLyricSettings={setLyricSettings}
              />
            </div>
          )}
        </div>

        {/* 取消面板歌词/mini 歌词，字幕统一叠加在视频/封面上 */}

        <PlaylistPanel
          isMinimized={isMinimized}
          showPlaylist={showPlaylist}
          playlist={playlist}
          currentMusic={currentMusic}
          playlistPanelRef={playlistPanelRef}
          onPlaylistChange={onPlaylistChange}
          onRemoveFromPlaylist={onRemoveFromPlaylist}
          setPlaylist={setPlaylist}
          getCurrentLanguageTitle={getCurrentLanguageTitle}
          formatTime={formatTime}
          t={t}
          i18n={i18n}
          spaNavigate={spaNavigate}
        />
      </div>

      {/* 下载对话框 */}
      <DownloadDialog
        music={currentMusic}
        open={downloadDialogOpen}
        onClose={() => props.setDownloadDialogOpen?.(false)}
        onConfirm={handleDownloadConfirm}
      />
    </>
  );
}

